import React, { useState, useEffect } from 'react';
import {
  Card, Button, Form, Input, message, Tag, Space,
  Row, Col, Statistic, Popconfirm, Typography, Select, Avatar, Grid, Badge, Empty
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined,
  VideoCameraOutlined, SoundOutlined, CustomerServiceOutlined, HeartOutlined,
  ArrowLeftOutlined, StarOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// 配置axios基础URL，指向后端服务
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';
const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const Entertainment = () => {
  const navigate = useNavigate();
  const [entertainment, setEntertainment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [stats, setStats] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchEntertainment();
    fetchStats();
  }, []);

  const fetchEntertainment = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/entertainment', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '')}`
        }
      });
      if (response.data.success) {
        setEntertainment(response.data.entertainment);
      }
    } catch (error) {
      // 未登录时不显示错误提示
      const token = localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '');
      if (token) {
        message.error('获取娱乐项目列表失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/entertainment/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '')}`
        }
      });
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      // 静默处理统计错误
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingItem) {
        const response = await axios.put(`/entertainment/${editingItem.id}`, values, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '')}`
          }
        });
        if (response.data.success) {
          message.success('娱乐项目更新成功');
        }
      } else {
        const response = await axios.post('/entertainment', values, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '')}`
          }
        });
        if (response.data.success) {
          message.success('娱乐项目添加成功');
        }
      }

      fetchEntertainment();
      fetchStats();
      setModalVisible(false);
      setEditingItem(null);
      form.resetFields();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`/entertainment/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '')}`
        }
      });
      if (response.data.success) {
        message.success('娱乐项目删除成功');
        fetchEntertainment();
        fetchStats();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };



  const columns = [
    {
      title: '娱乐名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar icon={getTypeIcon(record.type)} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={getTypeColor(type)}>
          {getTypeIcon(type)} {type}
        </Tag>
      ),
    },
    {
      title: '设备',
      dataIndex: 'device',
      key: 'device',
      render: (device) => device || '-',
    },
    {
      title: '时间段',
      dataIndex: 'time_period',
      key: 'time_period',
      render: (time_period) => time_period || '-',
    },
    {
      title: '备注说明',
      dataIndex: 'remark',
      key: 'remark',
      render: (remark) => remark || '-',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at) => new Date(created_at).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个娱乐项目吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredEntertainment = entertainment.filter((item) => {
    const matchType = typeFilter === 'all' || item.type === typeFilter;
    const kw = searchText.trim().toLowerCase();
    const matchKw = !kw || [item.name, item.type, item.device, item.time_period, item.remark]
      .some((f) => (f || '').toLowerCase().includes(kw));
    return matchType && matchKw;
  });

  const statistics = {
    total: entertainment.length,
    game: stats.find(s => s.type === '游戏')?.count || 0,
    movie: stats.find(s => s.type === '电影')?.count || 0,
    singing: stats.find(s => s.type === '唱歌')?.count || 0,
    instrument: stats.find(s => s.type === '乐器')?.count || 0,
    hobby: stats.find(s => s.type === '兴趣爱好')?.count || 0,
  };

  // 获取类型图标
  const getTypeIcon = (type) => {
    switch (type) {
      case '游戏':
        return <PlayCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />;
      case '电影':
        return <VideoCameraOutlined style={{ fontSize: '24px', color: '#52c41a' }} />;
      case '唱歌':
        return <SoundOutlined style={{ fontSize: '24px', color: '#faad14' }} />;
      case '乐器':
        return <CustomerServiceOutlined style={{ fontSize: '24px', color: '#722ed1' }} />;
      case '兴趣爱好':
        return <HeartOutlined style={{ fontSize: '24px', color: '#f5222d' }} />;
      default:
        return <PlayCircleOutlined style={{ fontSize: '24px', color: '#666' }} />;
    }
  };

  // 获取类型颜色
  const getTypeColor = (type) => {
    switch (type) {
      case '游戏':
        return '#1890ff';
      case '电影':
        return '#52c41a';
      case '唱歌':
        return '#faad14';
      case '乐器':
        return '#722ed1';
      case '兴趣爱好':
        return '#f5222d';
      default:
        return '#666';
    }
  };

  return (
    <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
            style={{ borderRadius: 10 }}
          >
            返回
          </Button>
          <div>
            <h1 className="mod-header-title">
              <PlayCircleOutlined style={{ marginRight: 8, color: '#6366F1' }} />
              娱乐管理
            </h1>
            <div className="mod-header-subtitle">记录你的游戏、影音与兴趣爱好，管理专属娱乐清单</div>
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingItem(null);
            form.resetFields();
            setModalVisible(true);
          }}
          style={{
            borderRadius: 10,
            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
            border: 'none',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
          }}
        >
          添加娱乐项目
        </Button>
      </div>

      {/* 统计卡 */}
      <div className="mod-stats">
        <div
          className={`mod-stat-card${typeFilter !== 'all' ? ' clickable' : ''}`}
          onClick={() => setTypeFilter('all')}
        >
          <div className="mod-stat-icon">🎯</div>
          <div>
            <div className="mod-stat-value">{statistics.total}</div>
            <div className="mod-stat-label">全部项目</div>
          </div>
        </div>
        <div className="mod-stat-card clickable" onClick={() => setTypeFilter('游戏')}>
          <div className="mod-stat-icon">🎮</div>
          <div>
            <div className="mod-stat-value">{statistics.game}</div>
            <div className="mod-stat-label">游戏</div>
          </div>
        </div>
        <div className="mod-stat-card clickable" onClick={() => setTypeFilter('电影')}>
          <div className="mod-stat-icon">🎬</div>
          <div>
            <div className="mod-stat-value">{statistics.movie}</div>
            <div className="mod-stat-label">电影</div>
          </div>
        </div>
        <div className="mod-stat-card clickable" onClick={() => setTypeFilter('唱歌')}>
          <div className="mod-stat-icon">🎤</div>
          <div>
            <div className="mod-stat-value">{statistics.singing}</div>
            <div className="mod-stat-label">唱歌</div>
          </div>
        </div>
        <div className="mod-stat-card clickable" onClick={() => setTypeFilter('乐器')}>
          <div className="mod-stat-icon">🎸</div>
          <div>
            <div className="mod-stat-value">{statistics.instrument}</div>
            <div className="mod-stat-label">乐器</div>
          </div>
        </div>
        <div className="mod-stat-card clickable" onClick={() => setTypeFilter('兴趣爱好')}>
          <div className="mod-stat-icon">❤️</div>
          <div>
            <div className="mod-stat-value">{statistics.hobby}</div>
            <div className="mod-stat-label">兴趣爱好</div>
          </div>
        </div>
      </div>

      {/* 工具栏 */}
      <Card className="mod-toolbar" bodyStyle={{ padding: 14 }}>
        <div className="mod-toolbar-row">
          <Input
            allowClear
            placeholder="搜索娱乐名称、设备、备注…"
            prefix={<StarOutlined style={{ color: '#bbb' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 260, maxWidth: '100%', borderRadius: 10 }}
          />
          <Select
            value={typeFilter}
            onChange={setTypeFilter}
            style={{ width: 160 }}
          >
            <Option value="all">全部类型</Option>
            <Option value="游戏">游戏</Option>
            <Option value="电影">电影</Option>
            <Option value="唱歌">唱歌</Option>
            <Option value="乐器">乐器</Option>
            <Option value="兴趣爱好">兴趣爱好</Option>
          </Select>
        </div>
      </Card>

      {/* 卡片网格 */}
      <div>
        <Row gutter={[16, 16]}>
          {filteredEntertainment.map((item) => (
            <Col xs={12} sm={8} md={6} lg={6} key={item.id}>
              <div className="mod-card">
                {/* 图标和类型标签 */}
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <div
                    className="mod-card-icon"
                    style={{ margin: '0 auto', width: 52, height: 52, fontSize: 26 }}
                  >
                    {getTypeIcon(item.type)}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Tag
                      color={getTypeColor(item.type)}
                      style={{ borderRadius: 10, fontSize: 12, padding: '2px 10px', marginRight: 0 }}
                    >
                      {item.type}
                    </Tag>
                  </div>
                </div>

                {/* 项目名称 */}
                <div style={{ textAlign: 'center', marginBottom: 6 }}>
                  <Text strong style={{ fontSize: 14, color: '#1f2430' }}>
                    {item.name}
                  </Text>
                </div>

                {/* 描述 */}
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <Text style={{ fontSize: 12, color: '#8c8c8c', lineHeight: 1.5 }}>
                    {item.description?.length > 30
                      ? `${item.description.substring(0, 30)}...`
                      : item.description || '暂无描述'}
                  </Text>
                </div>

                {/* 操作按钮 */}
                <div className="mod-card-actions" style={{ justifyContent: 'space-around' }}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingItem(item);
                      form.setFieldsValue(item);
                      setModalVisible(true);
                    }}
                    style={{ color: '#6366F1' }}
                  >
                    编辑
                  </Button>
                  <Popconfirm
                    title="确定删除这个娱乐项目吗？"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      handleDelete(item.id);
                    }}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                    >
                      删除
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* 空状态 */}
        {filteredEntertainment.length === 0 && !loading && (
          <Empty
            style={{ padding: '60px 20px' }}
            description={entertainment.length === 0 ? '暂无娱乐项目，点击右上角添加你的第一个娱乐项目' : '没有符合条件的娱乐项目'}
          />
        )}
      </div>

      {/* 添加/编辑娱乐项目模态框 */}
      {modalVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                {editingItem ? '编辑娱乐项目' : '添加娱乐项目'}
              </h3>
              <Button
                type="text"
                onClick={() => {
                  setModalVisible(false);
                  setEditingItem(null);
                  form.resetFields();
                }}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>

            <Form form={form} layout="vertical">
              <Form.Item
                name="name"
                label="娱乐名称"
                rules={[{ required: true, message: '请输入娱乐名称' }]}
              >
                <Input placeholder="请输入娱乐名称" />
              </Form.Item>

              <Form.Item
                name="type"
                label="娱乐类型"
                rules={[{ required: true, message: '请选择娱乐类型' }]}
              >
                <Select placeholder="请选择娱乐类型">
                  <Option value="游戏">游戏</Option>
                  <Option value="电影">电影</Option>
                  <Option value="唱歌">唱歌</Option>
                  <Option value="乐器">乐器</Option>
                  <Option value="兴趣爱好">兴趣爱好</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="device"
                label="设备"
              >
                <Input placeholder="请输入使用的设备（如：手机、电脑、电视等）" />
              </Form.Item>

              <Form.Item
                name="time_period"
                label="时间段"
              >
                <Input placeholder="请输入娱乐时间段（如：晚上8-10点）" />
              </Form.Item>

              <Form.Item
                name="remark"
                label="备注说明"
              >
                <TextArea rows={3} placeholder="请输入备注说明" />
              </Form.Item>
            </Form>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingItem(null);
                form.resetFields();
              }}>
                取消
              </Button>
              <Button type="primary" onClick={handleSubmit}>
                确定
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Entertainment;
